/**
 * Message Reaction Picker Component
 * 
 * Quick emoji reactions for messages (appears on right-click/long-press).
 * Shows common reactions + option to open full picker.
 */

import React, { useRef, useEffect, useState } from "react";
import EmojiPickerLib, { EmojiClickData, Theme } from "emoji-picker-react";

// Quick reaction emojis
const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

interface MessageReactionPickerProps {
  isOpen: boolean;
  position: { x: number; y: number };
  onReactionSelect: (emoji: string) => void;
  onClose: () => void;
}

/**
 * MessageReactionPicker - Quick reactions popup for messages
 */
const MessageReactionPicker: React.FC<MessageReactionPickerProps> = ({
  isOpen,
  position,
  onReactionSelect,
  onClose,
}) => {
  const [showFullPicker, setShowFullPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        onClose();
        setShowFullPicker(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Reset full picker when closed
  useEffect(() => {
    if (!isOpen) {
      setShowFullPicker(false);
    }
  }, [isOpen]);

  // Handle quick reaction click
  const handleQuickReaction = (emoji: string) => {
    onReactionSelect(emoji);
    onClose();
  };

  // Handle full picker emoji selection
  const handleFullPickerSelect = (emojiData: EmojiClickData) => {
    onReactionSelect(emojiData.emoji);
    onClose();
    setShowFullPicker(false);
  };

  if (!isOpen) return null;

  return (
    <div
      ref={pickerRef}
      className="fixed z-[100]"
      style={{
        left: Math.min(position.x, window.innerWidth - 350),
        top: Math.min(position.y, window.innerHeight - (showFullPicker ? 450 : 60)),
      }}
    >
      {showFullPicker ? (
        // Full emoji picker
        <div className="shadow-xl rounded-xl overflow-hidden">
          <EmojiPickerLib
            onEmojiClick={handleFullPickerSelect}
            theme={Theme.LIGHT}
            width={320}
            height={400}
            searchPlaceholder="Search emoji..."
            previewConfig={{ showPreview: false }}
          />
        </div>
      ) : (
        // Quick reactions bar
        <div className="bg-white rounded-full shadow-xl border border-slate-200 px-2 py-1.5 flex items-center gap-1">
          {QUICK_REACTIONS.map((emoji) => (
            <button
              key={emoji}
              onClick={() => handleQuickReaction(emoji)}
              className="w-9 h-9 flex items-center justify-center text-xl hover:bg-slate-100 rounded-full transition-all hover:scale-110"
              title={`React with ${emoji}`}
            >
              {emoji}
            </button>
          ))}
          
          {/* More emojis button */}
          <button
            onClick={() => setShowFullPicker(true)}
            className="w-9 h-9 flex items-center justify-center text-slate-400 hover:bg-slate-100 rounded-full transition-colors"
            title="More emojis"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default MessageReactionPicker;
